import classNames from "classnames/bind";
import { format, parse } from "date-fns";
import type { ChangeEvent } from "react";
import React, { useState } from "react";
import Button from "../../../Button/Button";
import { ButtonToggleGroup } from "../../../ButtonToggleGroup";
import { getValidHour, getValidMinute } from "../../../utils/timeFormatter";
import styles from "../DateSelector.module.scss";
import {
  DAY_STRING_FORMAT,
  DAY_TIME_STRING_FORMAT,
} from "../dateSelectorConstants";
import type { TimePickerProps } from "../types";

const cx = classNames.bind(styles);

const TimePicker = ({
  value,
  onChange,
  onClose,
  isStart,
  isClearable,
}: TimePickerProps) => {
  const [hour, setHour] = useState(format(value, "h"));
  const [minute, setMinute] = useState(format(value, "mm"));
  const [isAM, setIsAm] = useState(format(value, "a") === "AM");

  const onChangeHour = ({
    target: { value: newHour },
  }: ChangeEvent<HTMLInputElement>) => {
    const validHour = getValidHour(newHour);
    setHour(validHour);
  };

  const onBlurHour = () => {
    // do nothing because we have already validated the hour
  };

  const onChangeMinute = ({
    target: { value: newMinute },
  }: ChangeEvent<HTMLInputElement>) => {
    const validMinute = getValidMinute(newMinute);
    setMinute(validMinute);
  };

  const onBlurMinute = () => {
    // do nothing because we have already validated the minute
  };

  const toggleAM = () => {
    setIsAm(!isAM);
  };

  const onSubmit = () => {
    if (value) {
      const stringValue = format(value, DAY_STRING_FORMAT);

      const newValue = parse(
        `${stringValue} ${hour}:${minute} ${isAM ? "AM" : "PM"}`,
        DAY_TIME_STRING_FORMAT,
        new Date()
      );

      onChange(newValue);

      onClose();
    }
  };

  return (
    <div className={cx("timeContainer")}>
      <h1 className={cx("timeHeader")}>
        {isStart === true ? "Start" : null} {isStart === false ? "End" : null}{" "}
        Time
      </h1>

      <div className={cx("timeInputContainer")}>
        <div className={cx("timeInput")}>
          <input
            autoFocus
            value={hour}
            onChange={onChangeHour}
            onBlur={onBlurHour}
            type="number"
            onFocus={(e) => e.currentTarget.select()} // compliments autoFocus and hilights the input
          />
          :
          <input
            value={minute}
            onChange={onChangeMinute}
            onBlur={onBlurMinute}
            type="number"
          />
        </div>

        <ButtonToggleGroup
          name="am-pm-button-toggle-group"
          activeKey={isAM ? "AM" : "PM"}
          items={[
            { dataKey: "AM", label: "AM" },
            { dataKey: "PM", label: "PM" },
          ]}
          onChange={toggleAM}
        />
      </div>

      <div className={cx("timeActionContainer")}>
        {isClearable && (
          <Button
            variant="outline"
            onClick={() => {
              onChange(undefined);
              onClose();
            }}
            className={cx("clear")}
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={onSubmit}
        >
          OK
        </Button>
      </div>
    </div>
  );
};

export default TimePicker;
